import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { AuthService } from "../../services/auth/auth.service";
import {
  CompanyTrialStatusApiService,
  TrialStatus,
} from "../../services/company/company-trial-status-api.service";

/**
 * Full-width strip at the very top of the app frame (mounted in app.component.html,
 * above app-layout, the same slot as the announcement TOP_BANNER) — not a small header
 * pill any more. Matches the reference shape: a bold, unmissable day count instead of
 * something that can get lost in the header's action row.
 *
 * Two states once a company actually had a trial (trialStatus.endsAt is set):
 * - active: the "X days left" countdown.
 * - expired: plain text noting that editing is turned off — no button, no mailto link,
 *   nothing shaped like a toast/popup. The actual access change (view-only roles) is
 *   handled entirely server-side, via permissions, in
 *   BusinessRoleAccessService#toViewOnly / CompanyService#downgradeToReadOnlyIfTrialExpired
 *   — this banner is informational text only.
 * A company that never had a trial at all (endsAt undefined — admin-created, or
 * predates the trial system) shows nothing, in either state.
 */
@Component({
  selector: "app-trial-banner",
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: "./trial-banner.component.html",
  styleUrl: "./trial-banner.component.scss",
})
export class TrialBannerComponent implements OnInit {
  trialStatus: TrialStatus | undefined;

  constructor(
    private authService: AuthService,
    private trialStatusService: CompanyTrialStatusApiService,
  ) {}

  /** Had a real trial (as opposed to no trial at all) — the only case this banner ever renders. */
  get hadTrial(): boolean {
    return !!this.trialStatus?.endsAt;
  }

  get isExpired(): boolean {
    return this.hadTrial && !this.trialStatus?.active;
  }

  get show(): boolean {
    return !!this.trialStatus?.active || this.isExpired;
  }

  get daysRemaining(): number {
    return this.trialStatus?.daysRemaining ?? 0;
  }

  ngOnInit(): void {
    if (!this.authService.getCompanyId()) {
      return;
    }
    this.trialStatusService.getTrialStatus().subscribe({
      next: (status) => (this.trialStatus = status),
      error: () => (this.trialStatus = undefined),
    });
  }
}
